import { Select, Skeleton } from "antd";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { formatDate } from "../../services/util";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

const { Option } = Select;

const BAR_COLORS = [
    "var(--color-primary)",
    "var(--color-accent)",
    "var(--color-warning)",
    "var(--color-primary-hover)",
    "var(--color-success)",
    "var(--color-error)",
];

function transformColumnChartData(rawData) {
    if (!rawData || rawData.length < 2) return { data: [], keys: [], xKey: "" };
    const [headers, ...rows] = rawData;
    const [xKey, ...seriesKeys] = headers;
    const data = rows.map((row) => {
        const obj = { [xKey]: String(row[0]) };
        seriesKeys.forEach((key, i) => {
            obj[key] = Number(row[i + 1]) || 0;
        });
        return obj;
    });
    return { data, keys: seriesKeys, xKey };
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "var(--color-bg-control)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
                boxShadow: "var(--shadow-card)",
            }}
        >
            <p
                style={{
                    margin: 0,
                    fontWeight: 600,
                    marginBottom: 6,
                    color: "var(--color-text-secondary)",
                    fontSize: 11,
                }}
            >
                {label}
            </p>
            {payload.map((entry) => (
                <p
                    key={entry.name}
                    style={{ margin: "2px 0", color: entry.fill }}
                >
                    {entry.name}:{" "}
                    <strong style={{ color: "var(--color-text)" }}>
                        {entry.value}
                    </strong>
                </p>
            ))}
        </div>
    );
};

const axisStyle = {
    tick: { fill: "var(--color-text-muted)", fontSize: 11 },
    axisLine: { stroke: "var(--color-border)" },
    tickLine: false,
};

function ColumnChart({ rawData, height = 340 }) {
    const { data, keys, xKey } = transformColumnChartData(rawData);
    if (!data.length) return null;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart
                data={data}
                margin={{ top: 10, right: 16, left: 0, bottom: 5 }}
                barCategoryGap="30%"
            >
                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border-light)"
                    vertical={false}
                />
                <XAxis dataKey={xKey} {...axisStyle} />
                <YAxis {...axisStyle} allowDecimals={false} />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "var(--color-primary-light)" }}
                />
                <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                    formatter={(v) => (
                        <span style={{ color: "var(--color-text-secondary)" }}>
                            {v}
                        </span>
                    )}
                />
                {keys.map((key, i) => (
                    <Bar
                        key={key}
                        dataKey={key}
                        fill={BAR_COLORS[i % BAR_COLORS.length]}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={56}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}

// ── Shared section wrapper ────────────────────────────
function ChartSection({ title, filterNode, isLoading, children }) {
    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "20px 24px",
                marginBottom: 20,
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 16,
                }}
            >
                <div
                    style={{
                        width: 3,
                        height: 18,
                        borderRadius: 2,
                        background: "var(--gradient-primary)",
                    }}
                />
                <span
                    style={{
                        color: "var(--color-text)",
                        fontWeight: 600,
                        fontSize: 15,
                    }}
                >
                    {title}
                </span>
            </div>

            {/* Filter bar */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    background: "var(--color-bg-control)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    padding: "10px 16px",
                    marginBottom: 20,
                }}
            >
                <span
                    style={{
                        color: "var(--color-text-muted)",
                        fontSize: 12,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                    }}
                >
                    Hotels
                </span>
                <div style={{ flex: 1 }}>{filterNode}</div>
            </div>

            {isLoading ? (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 8, width: "100%" }}
                />
            ) : (
                children
            )}
        </div>
    );
}

function CheckInAnalytics() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;

    const defaultFormData = {
        staff_id,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        all_selected: true,
        hotel_ids: [],
    };

    const [hotelOptions, setHotelOptions] = useState([]);
    const [formDataTimeZone, setFormDataTimeZone] = useState(defaultFormData);
    const [timeZoneChartData, setTimeZoneChartData] = useState([]);
    const [formDataShift, setFormDataShift] = useState(defaultFormData);
    const [shiftChartData, setShiftChartData] = useState([]);

    const {
        status: checkInByTimeZoneStatus,
        data: checkInByTimeZoneRes,
        refetch: getCheckInByTimeZone,
    } = useQuery(
        "checkInsByTimeZone",
        async () =>
            await HTTP("/analytics/getAvgCheckinsTimezoneWise", {
                ...formDataTimeZone,
            }),
        { enabled: false },
    );
    const {
        status: checkInByShiftStatus,
        data: checkInByShiftRes,
        refetch: getCheckInByShift,
    } = useQuery(
        "checkInsByShift",
        async () =>
            await HTTP("/analytics/getAvgCheckinsShiftWise", {
                ...formDataShift,
            }),
        { enabled: false },
    );
    const {
        status: hotelStatus,
        data: hotelData,
        refetch: getHotelSelectionOptions,
    } = useQuery(
        "analytics-hotels",
        async () =>
            await HTTP("/dashboard/getHotels", { staff_id, hotel_type: "all" }),
        { enabled: false },
    );

    useEffect(() => {
        getHotelSelectionOptions();
        getCheckInByTimeZone();
        getCheckInByShift();
    }, []);

    useEffect(() => {
        const [s, e] = selectedDates;
        const dates = { start_date: formatDate(s), end_date: formatDate(e) };
        setFormDataTimeZone((prev) => ({ ...prev, ...dates }));
        setFormDataShift((prev) => ({ ...prev, ...dates }));
    }, [selectedDates]);

    useEffect(() => {
        if (checkInByTimeZoneStatus === "success")
            setTimeZoneChartData(checkInByTimeZoneRes.data);
    }, [checkInByTimeZoneStatus, checkInByTimeZoneRes]);

    useEffect(() => {
        if (checkInByShiftStatus === "success") {
            const { data: chartData, success } = checkInByShiftRes;
            if (success) {
                setShiftChartData(
                    chartData.map((e, i) => {
                        if (i > 0) e[1] = parseFloat(e[1]);
                        return e;
                    }),
                );
            }
        }
    }, [checkInByShiftRes, checkInByShiftStatus]);

    useEffect(() => {
        if (hotelStatus === "success") setHotelOptions(hotelData.data);
    }, [hotelStatus, hotelData]);
    useEffect(() => {
        getCheckInByTimeZone();
    }, [formDataTimeZone]);
    useEffect(() => {
        getCheckInByShift();
    }, [formDataShift]);

    const hotelSelectOptions = hotelOptions.map((h) => (
        <Option key={h.hotel_id || h.id} value={h.hotel_id || h.id}>
            {`${h.hotel_id || h.id} - ${h.hotel_name || h.name} - ${h.city}`}
        </Option>
    ));

    const isLoadingTimeZone =
        checkInByTimeZoneStatus === "loading" ||
        checkInByTimeZoneStatus === "idle";
    const isLoadingShift =
        checkInByShiftStatus === "loading" || checkInByShiftStatus === "idle";

    return (
        <div style={{ padding: "4px 0" }}>
            <ChartSection
                title="Average Check-ins by Timezone"
                isLoading={isLoadingTimeZone}
                filterNode={
                    <Select
                        placeholder="All hotels"
                        showSearch
                        mode="multiple"
                        loading={!hotelOptions.length}
                        style={{ width: "100%" }}
                        onChange={(e) =>
                            setFormDataTimeZone((prev) => ({
                                ...prev,
                                hotel_ids: e,
                                all_selected: e.length === 0,
                            }))
                        }
                        filterOption={(input, option) =>
                            option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                    >
                        {hotelSelectOptions}
                    </Select>
                }
            >
                {timeZoneChartData.length > 0 ? (
                    <ColumnChart rawData={timeZoneChartData} height={340} />
                ) : (
                    <Skeleton
                        active
                        title={false}
                        paragraph={{ rows: 8, width: "100%" }}
                    />
                )}
            </ChartSection>

            <ChartSection
                title="Average Check-ins by Shift"
                isLoading={isLoadingShift}
                filterNode={
                    <Select
                        placeholder="All hotels"
                        showSearch
                        mode="multiple"
                        loading={!hotelOptions.length}
                        style={{ width: "100%" }}
                        onChange={(e) =>
                            setFormDataShift((prev) => ({
                                ...prev,
                                hotel_ids: e,
                                all_selected: e.length === 0,
                            }))
                        }
                        filterOption={(input, option) =>
                            option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                    >
                        {hotelSelectOptions}
                    </Select>
                }
            >
                {shiftChartData.length > 0 ? (
                    <ColumnChart rawData={shiftChartData} height={340} />
                ) : (
                    <Skeleton
                        active
                        title={false}
                        paragraph={{ rows: 8, width: "100%" }}
                    />
                )}
            </ChartSection>
        </div>
    );
}

export { CheckInAnalytics };
